 import { useState } from "react";
 import { Pencil, Trash2, ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
 import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
 import { PartnerCodeRedemptionsList } from "./PartnerCodeRedemptionsList";
import { format } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";

type PartnerCode = Tables<"partner_codes">;

export type PartnerCodeSortField =
  | "created_at"
  | "code"
  | "partner_name"
  | "discount_type"
  | "current_uses"
  | "valid_until";

interface PartnerCodesTableProps {
  codes: PartnerCode[];
  isLoading: boolean;
  isEmpty: boolean;
  sortField: PartnerCodeSortField;
  sortDirection: "asc" | "desc";
  onSort: (field: PartnerCodeSortField) => void;
  onEdit: (code: PartnerCode) => void;
  onDelete: (code: PartnerCode) => void;
}

export const PartnerCodesTable = ({
  codes,
  isLoading,
  isEmpty,
  sortField,
  sortDirection,
  onSort,
  onEdit,
  onDelete,
}: PartnerCodesTableProps) => {
   const [expandedCodeId, setExpandedCodeId] = useState<string | null>(null);
 
   const toggleExpand = (codeId: string) => {
     setExpandedCodeId(expandedCodeId === codeId ? null : codeId);
   };
 
  const renderSortButton = (field: PartnerCodeSortField, label: string) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-2 text-xs font-medium"
      onClick={() => onSort(field)}
    >
      {label}
      <ArrowUpDown
        className={`h-3 w-3 ${sortField === field ? "text-primary" : "text-muted-foreground"}`}
      />
    </Button>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No partner codes found. Create one to get started.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
         <TableHead className="h-9 w-8"></TableHead>
          <TableHead className="h-9">{renderSortButton("code", "Code")}</TableHead>
          <TableHead className="h-9">{renderSortButton("partner_name", "Partner")}</TableHead>
          <TableHead className="h-9">{renderSortButton("discount_type", "Type")}</TableHead>
          <TableHead className="h-9">Value</TableHead>
          <TableHead className="h-9">Status</TableHead>
          <TableHead className="h-9">{renderSortButton("current_uses", "Uses")}</TableHead>
          <TableHead className="h-9">{renderSortButton("valid_until", "Valid Period")}</TableHead>
          <TableHead className="h-9">{renderSortButton("created_at", "Created")}</TableHead>
          <TableHead className="h-9 text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {codes.map((code) => (
         <Collapsible key={code.id} asChild open={expandedCodeId === code.id}>
           <>
             <TableRow className="h-9">
               <TableCell className="py-1 w-8">
                 <CollapsibleTrigger asChild>
                   <Button
                     variant="ghost"
                     size="sm"
                     className="h-6 w-6 p-0"
                     onClick={() => toggleExpand(code.id)}
                   >
                     {expandedCodeId === code.id ? (
                       <ChevronDown className="h-3.5 w-3.5" />
                     ) : (
                       <ChevronRight className="h-3.5 w-3.5" />
                     )}
                   </Button>
                 </CollapsibleTrigger>
               </TableCell>
               <TableCell className="py-1 font-mono text-sm font-bold">{code.code}</TableCell>
            <TableCell className="py-1 whitespace-nowrap">{code.partner_name}</TableCell>
            <TableCell className="py-1">
              <Badge
                variant="outline"
                className={
                  code.discount_type === "credits"
                    ? "bg-info/10 text-info border-info/30"
                    : "bg-secondary/10 text-secondary border-secondary/30"
                }
              >
                {code.discount_type === "credits" ? "Credits" : "Percentage"}
              </Badge>
            </TableCell>
            <TableCell className="py-1 whitespace-nowrap">
              {code.discount_type === "credits"
                ? `${code.credits_value} credits`
                : `${code.discount_percentage}%`}
            </TableCell>
            <TableCell className="py-1">
              <Badge
                variant="outline"
                className={
                  code.is_active
                    ? "bg-success/10 text-success border-success/30"
                    : "bg-muted text-muted-foreground border-border"
                }
              >
                {code.is_active ? "Active" : "Inactive"}
              </Badge>
            </TableCell>
            <TableCell className="py-1 whitespace-nowrap">
              {code.current_uses || 0}
              {code.max_uses ? ` / ${code.max_uses}` : ""}
            </TableCell>
            <TableCell className="py-1 whitespace-nowrap text-xs">
              {code.valid_from && code.valid_until
                ? `${format(new Date(code.valid_from), "MMM d")} - ${format(new Date(code.valid_until), "MMM d, yyyy")}`
                : "No expiry"}
            </TableCell>
            <TableCell className="py-1 whitespace-nowrap text-xs text-muted-foreground">
              {code.created_at ? format(new Date(code.created_at), "MMM d, yyyy") : "-"}
            </TableCell>
               <TableCell className="py-1 text-right">
                 <div className="flex justify-end gap-1">
                   <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(code)}>
                     <Pencil className="h-3.5 w-3.5" />
                   </Button>
                   <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onDelete(code)}>
                     <Trash2 className="h-3.5 w-3.5" />
                   </Button>
                 </div>
               </TableCell>
             </TableRow>
             <CollapsibleContent asChild>
               <TableRow className="bg-muted/20 hover:bg-muted/30">
                 <TableCell colSpan={10} className="py-3 px-4">
                   <PartnerCodeRedemptionsList partnerCodeId={code.id} />
                 </TableCell>
               </TableRow>
             </CollapsibleContent>
           </>
         </Collapsible>
        ))}
      </TableBody>
    </Table>
  );
};
